/**
 * Input Atom - Standardized input component
 *
 * Basic text input element following atomic design principles.
 * Extends Chakra UI's input with consistent API and validation states.
 */

import type { InputProps as ChakraInputProps } from "@chakra-ui/react"
import { Input as ChakraInput } from "@chakra-ui/react"
import * as React from "react"

export interface InputProps extends ChakraInputProps {
  // Required props (none for input)

  // Optional props with sensible defaults
  variant?: "outline" | "subtle" | "flushed"
  size?: "xs" | "sm" | "md" | "lg"

  // State props
  isInvalid?: boolean
  isDisabled?: boolean
  isReadOnly?: boolean
  isRequired?: boolean

  // Event handlers
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void

  // Accessibility
  "aria-label"?: string
  "aria-describedby"?: string
  "aria-invalid"?: boolean

  // Styling escape hatch
  className?: string
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  function Input(props, ref) {
    const {
      variant = "outline",
      size = "md",
      isInvalid,
      isDisabled,
      isReadOnly,
      isRequired,
      ...rest
    } = props

    return (
      <ChakraInput
        ref={ref}
        variant={variant}
        size={size}
        disabled={isDisabled}
        readOnly={isReadOnly}
        required={isRequired}
        aria-invalid={isInvalid}
        {...rest}
      />
    )
  },
)

Input.displayName = "Input"

export default Input
